import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../lib/api';
import { LoadingTable, ErrorState, EmptyState } from './States';
import { DataTable } from './DataTable';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { RefreshCw, Search } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return iso; }
}
const ROLE = { admin: 'Direzione', control: 'Controllo' };

function statusBadge(s) {
  if (s >= 200 && s < 300) return <Badge className="bg-[hsl(var(--success))] text-white">{s}</Badge>;
  if (s === 401 || s === 403) return <Badge variant="outline" className="bg-[hsl(var(--danger))]/12 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30">{s}</Badge>;
  if (s >= 400) return <Badge variant="outline" className="bg-[hsl(var(--warning))]/14 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/30">{s}</Badge>;
  return <Badge variant="secondary">{s || '—'}</Badge>;
}

export default function AuditPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback((action) => {
    setError(false); setLoading(true);
    api.auditList({ limit: 300, ...(action ? { action } : {}) })
      .then(setData).catch(() => setError(true)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'ts', header: 'Data e ora', render: (r) => <span className="font-medium tabular">{fmtDate(r.ts)}</span>, sortValue: (r) => r.ts },
    { key: 'email', header: 'Utente', render: (r) => <span className="text-xs">{r.email || 'anonimo'}</span> },
    { key: 'role', header: 'Ruolo', render: (r) => r.role ? <Badge variant="secondary" className="text-[10px]">{ROLE[r.role] || r.role}</Badge> : '—', sortValue: (r) => r.role || '' },
    { key: 'action', header: 'Azione', render: (r) => <span className="font-medium">{r.action}</span> },
    { key: 'path', header: 'Endpoint', render: (r) => <span className="text-[11px] text-muted-foreground">{r.method} {r.path}</span>, sortable: false },
    { key: 'status', header: 'Esito', align: 'right', render: (r) => statusBadge(r.status), sortValue: (r) => r.status },
  ];

  if (error) return <ErrorState message="Impossibile caricare il registro" onRetry={() => load()} />;
  if (!data) return <LoadingTable />;

  const logs = data.logs || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h3 className="text-sm font-semibold">Registro attività</h3><p className="text-xs text-muted-foreground mt-0.5">Accessi e operazioni di scrittura · {data.total} eventi</p></div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(q)} placeholder="Filtra per azione…" className="pl-8 w-56" data-testid="audit-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => load(q)} disabled={loading} data-testid="audit-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Aggiorna</Button>
        </div>
      </div>
      {logs.length === 0 ? <EmptyState title="Nessun evento" description="Le attività compariranno qui man mano che vengono eseguite." /> : <DataTable columns={columns} rows={logs} getRowId={(r) => r.id} />}
    </div>
  );
}
